import React from 'react';
import { IMaskInput } from 'react-imask';
import { FormWrapper, InputWrapper } from './Wrappers';

// Swedish domestic bank-account validation, ported from barsoom/banktools-se (a maintained gem used
// for Autogiro) and verified against its test suite. A clearing number identifies the bank and its
// account rules: expected serial length, and for two Nordea "personkonto" ranges a mod-10 (Luhn)
// check. Most banks use bank-internal mod-11 variants that this (like banktools-se) does NOT try to
// reproduce — so we validate length + known clearing + Luhn-where-it-applies, and let Visma be the
// final authority. That catches the obviously-wrong inputs without ever false-rejecting a valid one.
//   Source: https://github.com/barsoom/banktools-se  ·  https://www.bankgirot.se (Bankernas kontonummer)

type Bank = { name: string, serial?: [number, number], luhn?: boolean, clearingChecksum?: boolean, zerofill?: boolean }

// Clearing-number ranges → bank + account rules. `serial` = [min, max] serial digits (default [7, 7]).
const RANGES: [number, number, Bank][] = [
  [1100, 1199, { name: 'Nordea' }], [1200, 1399, { name: 'Danske Bank' }], [1400, 2099, { name: 'Nordea' }],
  [2300, 2399, { name: 'Ålandsbanken' }], [2400, 2499, { name: 'Danske Bank' }], [3000, 3299, { name: 'Nordea' }],
  [3300, 3300, { name: 'Nordea', serial: [10, 10], luhn: true }], [3301, 3399, { name: 'Nordea' }],
  [3400, 3409, { name: 'Länsförsäkringar Bank' }], [3410, 3781, { name: 'Nordea' }],
  [3782, 3782, { name: 'Nordea', serial: [10, 10], luhn: true }], [3783, 4999, { name: 'Nordea' }],
  [5000, 5999, { name: 'SEB' }], [6000, 6999, { name: 'Handelsbanken', serial: [8, 9] }], [7000, 7999, { name: 'Swedbank' }],
  [8000, 8999, { name: 'Swedbank', serial: [10, 10], clearingChecksum: true, zerofill: true }],
  [9020, 9029, { name: 'Länsförsäkringar Bank' }], [9040, 9049, { name: 'Citibank' }], [9060, 9069, { name: 'Länsförsäkringar Bank' }],
  [9090, 9099, { name: 'Royal Bank of Scotland' }], [9100, 9109, { name: 'Nordnet Bank' }], [9120, 9124, { name: 'SEB' }],
  [9130, 9149, { name: 'SEB' }], [9150, 9169, { name: 'Skandiabanken' }], [9170, 9179, { name: 'Ikano Bank' }],
  [9180, 9189, { name: 'Danske Bank', serial: [10, 10] }], [9190, 9199, { name: 'Den Norske Bank' }],
  [9230, 9239, { name: 'Marginalen Bank' }], [9250, 9259, { name: 'SBAB' }], [9260, 9269, { name: 'Den Norske Bank' }],
  [9270, 9279, { name: 'ICA Banken' }], [9280, 9289, { name: 'Resurs Bank' }],
  [9300, 9349, { name: 'Sparbanken Öresund', serial: [10, 10], zerofill: true }], [9400, 9449, { name: 'Forex Bank' }],
  [9460, 9469, { name: 'GE Money Bank' }], [9470, 9479, { name: 'Fortis Bank' }],
  [9500, 9549, { name: 'Nordea/Plusgirot', serial: [1, 10] }], [9550, 9569, { name: 'Avanza Bank' }],
  [9570, 9579, { name: 'Sparbanken Syd', serial: [10, 10], zerofill: true }], [9630, 9639, { name: 'Lån og Spar Bank Sverige' }],
  [9640, 9649, { name: 'Nordax Bank AB' }], [9650, 9659, { name: 'MedMera Bank' }], [9660, 9669, { name: 'Svea Bank' }],
  [9670, 9679, { name: 'JAK Medlemsbank' }], [9680, 9689, { name: 'Bluestep Finance' }], [9690, 9699, { name: 'Folkia' }],
  [9700, 9709, { name: 'Ekobanken' }], [9710, 9719, { name: 'Lunar Bank' }], [9720, 9729, { name: 'Netfonds Bank (ub)' }],
  [9750, 9759, { name: 'Northmill Bank' }], [9770, 9779, { name: 'FTCS' }], [9780, 9789, { name: 'Klarna Bank' }],
  [9955, 9955, { name: 'Kommuninvest' }], [9960, 9969, { name: 'Nordea/Plusgirot', serial: [1, 10] }],
]

const digits = (s: string) => s.replace(/\D/g, '');

function luhnValid(s: string): boolean {
  if (!/^\d+$/.test(s)) return false
  let sum = 0
  for (let i = 0; i < s.length; i++) {
    let d = s.charCodeAt(s.length - 1 - i) - 48
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9 }
    sum += d
  }
  return sum % 10 === 0
}

function bankFor(clearing: string): Bank | null {
  const d = digits(clearing)
  if (d.length < 4) return null
  const n = parseInt(d.slice(0, 4), 10)
  for (const [lo, hi, bank] of RANGES) if (n >= lo && n <= hi) return bank
  return null
}

export type BankAccountCheck = { ok: true, bank: string } | { ok: false, bank: string | null, reason: string }

// Validate a (clearing, account) pair. `bank` is the resolved bank name when the clearing is known.
export function checkBankAccount(clearing: string, account: string): BankAccountCheck {
  const bank = bankFor(clearing)
  if (bank === null) {
    return { ok: false, bank: null, reason: digits(clearing).length < 4 ? 'Ange clearingnummer (minst 4 siffror)' : 'Okänt clearingnummer' }
  }
  const [min, max] = bank.serial ?? [7, 7]
  const serial = bank.zerofill ? digits(account).padStart(max, '0') : digits(account)
  if (serial.length < min) return { ok: false, bank: bank.name, reason: `Kontonumret är för kort för ${bank.name}` }
  if (serial.length > max) return { ok: false, bank: bank.name, reason: `Kontonumret är för långt för ${bank.name}` }
  if (bank.luhn && !luhnValid(serial)) return { ok: false, bank: bank.name, reason: 'Kontrollsiffran i kontonumret stämmer inte' }
  return { ok: true, bank: bank.name }
}

// Field-level guards (format only) — the authoritative, bank-aware check is checkBankAccount, run
// in the dialog against both fields together.
export function isValidClearing(clearing: string) {
  return /^\d{4,5}$/.test(digits(clearing));
}
export function isValidBankAccountNumber(account: string) {
  return /^\d{1,14}$/.test(digits(account));
}

function makeDigitInput(mask: string) {
  return function DigitInputRaw({onChange, ...props}: {onChange?: (newValue: string) => void}) {
    return (
      <IMaskInput
        {...props}
        mask={mask}
        lazy={true}
        unmask={false}
        autoComplete='off'
        onAccept={(value, _mask, event) => {if (event !== undefined && onChange !== undefined) { onChange(value) }}}
      />
    );
  };
}

export const ClearingInput = FormWrapper(
  {Component: InputWrapper(makeDigitInput('0000[0]')), validationFunction: isValidClearing});

export const BankAccountNumberInput = FormWrapper(
  {Component: InputWrapper(makeDigitInput('0000[0000000000]')), validationFunction: isValidBankAccountNumber});
